import { useCallback, useEffect, useMemo, useState } from "react";

import { useGetQueueById, useGetSkills } from "@coherehealth/qm-api";
import { useSnackbar } from "notistack";
import { DateFilterOptions, defaultSkill } from "../util/ChartUtil";
import { H1, colorsLight, useParsedQueryString } from "@coherehealth/common";
import { useAuthorized } from "Authorization";
import { useNavigate, useParams, generatePath, useLocation } from "react-router-dom";
import routes from "routes";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import QmBreadcrumbs from "util/QmBreadcrumbs";
import QueueVolumeHistoricalProductionChartContent from "./QueueHistoricalProductionChartContent";
import QueueVolumeHistoricalStatsContent from "./QueueVolumeHistoricalStatsContent";
import { Divider } from "@mui/material";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    pageContent: {
      padding: "24px 32px 0px",
    },
  })
);

const QueueVolumeDashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const classes = useStyles();
  const { queueId } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const canAccessPage = useAuthorized("SUPERVISOR");

  const qs = useParsedQueryString();
  const initializedDate = useMemo(() => (qs.days ? (qs.days as DateFilterOptions) : "14"), [qs]);
  const initializedSkill = useMemo(() => (qs.skill ? (qs.skill as string) : "All"), [qs]);

  const [hasError, setHasError] = useState<boolean>(false);
  const [dateValue, setDateValue] = useState<DateFilterOptions>(initializedDate);
  const [skillValue, setSkillValue] = useState<string>(defaultSkill.id);

  const redirectSkillValue = skillValue === defaultSkill.id ? "All" : skillValue;

  const { data: queue, refetch: refetchQueue, error } = useGetQueueById({ queueId: queueId || "", lazy: true });
  const { data: skills } = useGetSkills({});

  const skillsOptions = useMemo(() => {
    const res = [defaultSkill];
    return res.concat(
      skills
        ? skills.map((skill) => {
            return {
              id: skill.name || "",
              label: skill.name || "",
            };
          })
        : []
    );
  }, [skills]);

  const queueTitle = queue?.displayConfig?.title || queue?.name || "";

  useEffect(() => {
    setDateValue(initializedDate);
    setSkillValue(initializedSkill === "All" ? defaultSkill.id : initializedSkill);
  }, [initializedDate, initializedSkill]);

  useEffect(() => {
    if (queueId) {
      refetchQueue();
    }
  }, [refetchQueue, queueId]);

  useEffect(() => {
    if (!queueId || error) {
      setHasError(true);
    }
    if (hasError) {
      enqueueSnackbar(`Failed to load data`, { variant: "error", preventDuplicate: true });
    }
  }, [enqueueSnackbar, error, hasError, queueId]);

  const breadCrumpRoutes = useMemo(() => {
    return [
      { to: generatePath(routes.REPORTS.DASHBOARD.concat(location.search)), label: "Reports" },
      { to: generatePath(location.pathname.concat(location.search)), label: queueTitle, disabled: true },
    ];
  }, [location.pathname, location.search, queueTitle]);

  const changeQueryStringValue = useCallback(
    //updates qs and navigates to route on new date selection
    (newDateValue: DateFilterOptions, skill?: string) => {
      navigate({
        pathname: generatePath(routes.REPORTS.QUEUE_VOLUME, { queueId: queue?.id }),
        search: `days=${newDateValue}&skill=${skill || redirectSkillValue}`,
      });
    },
    [navigate, queue?.id, redirectSkillValue]
  );

  useEffect(() => {
    //sets query string as 14 days as default on initial page load, and skill as All
    const shouldSetDays = !qs.days || (qs.days !== "1" && qs.days !== "7" && qs.days !== "14" && qs.days !== "28");
    const shouldSetSkill =
      !qs.skill || (!skillsOptions.findIndex((skill) => skill.id === qs.skill) && qs.skill !== "All");
    if (shouldSetDays && shouldSetSkill) {
      changeQueryStringValue("14", initializedSkill);
    } else if (shouldSetDays) {
      changeQueryStringValue("14", redirectSkillValue);
    } else if (shouldSetSkill) {
      changeQueryStringValue(dateValue, initializedSkill);
    }
  }, [changeQueryStringValue, dateValue, initializedSkill, qs, redirectSkillValue, skillValue, skillsOptions]);

  if (!canAccessPage) {
    return null;
  }

  return (
    <div style={{ backgroundColor: colorsLight.background.mid }}>
      <div className={classes.pageContent}>
        <QmBreadcrumbs routes={breadCrumpRoutes} />
        <H1 style={{ marginBottom: 16 }}>{queueTitle}</H1>
      </div>
      <Divider />
      <div className={classes.pageContent}>
        {queue && (
          <>
            <QueueVolumeHistoricalStatsContent
              queue={queue}
              setHasError={setHasError}
              skillValue={skillValue}
              skills={skills?.map((skill) => skill.name)}
            />
            <QueueVolumeHistoricalProductionChartContent
              queue={queue}
              dateValue={dateValue}
              setHasError={setHasError}
              skillsOptions={skillsOptions}
              skillValue={skillValue}
              setSkillValue={setSkillValue}
              changeQueryStringValue={changeQueryStringValue}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default QueueVolumeDashboard;
